/**
 * Reminder details - opened from the "Reminders on" button: who the reminders
 * go to, how many have gone, when the next one is due (or why none is), what
 * happens after the last one, and turning reminders off (behind a confirm).
 * Resource-agnostic.
 */
import { useEffect, useState } from 'react'
import { BellRing, CalendarClock, Mail, Repeat, Send } from 'lucide-react'
import { toast } from 'sonner'
import { useTranslation } from 'react-i18next'

import { Button } from '@/shared/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import { Separator } from '@/shared/components/ui/separator'
import { Switch } from '@/shared/components/ui/switch'
import { toastApiError } from '@/shared/lib/api-error'
import { useFormatters } from '@/shared/lib/formatting/use-formatters'

import {
  type AutoReminderStatus,
  useCancelReminder,
  useSetReminder,
} from './use-auto-reminder'

function Row({
  icon,
  label,
  children,
}: {
  icon?: React.ReactNode
  label: string
  children: React.ReactNode
}) {
  return (
    <div className="flex items-center justify-between gap-4 py-2">
      <span className="flex shrink-0 items-center gap-2 whitespace-nowrap text-[12px] text-[var(--ink-3)]">
        {icon}
        {label}
      </span>
      <span className="min-w-0 text-right text-[13px] text-[var(--ink)] wrap-anywhere">
        {children}
      </span>
    </div>
  )
}

export function AutoReminderDetailsModal({
  resourceType,
  uuid,
  status,
  open,
  onOpenChange,
}: {
  resourceType: string
  uuid: string
  status: AutoReminderStatus
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const { t } = useTranslation()
  const { formatDateTime } = useFormatters()
  const setReminder = useSetReminder(resourceType)
  const cancelReminder = useCancelReminder(resourceType)
  const [confirmCancel, setConfirmCancel] = useState(false)
  const canManage = status.can_manage !== false

  // Local cadence draft, seeded from status and re-seeded when it changes.
  const [interval, setInterval] = useState<string>('')
  const [maxCount, setMaxCount] = useState<string>('')
  useEffect(() => {
    setInterval(status.interval_days != null ? String(status.interval_days) : '')
    setMaxCount(status.max_reminders != null ? String(status.max_reminders) : '')
  }, [status.interval_days, status.max_reminders, open])

  const cadenceDirty =
    (status.interval_days != null ? String(status.interval_days) : '') !==
      interval ||
    (status.max_reminders != null ? String(status.max_reminders) : '') !== maxCount

  const saveCadence = () => {
    setReminder.mutate(
      {
        uuid,
        interval_days: interval === '' ? null : Number(interval),
        max_reminders: maxCount === '' ? null : Number(maxCount),
      },
      {
        onSuccess: () =>
          toast.success(t('reminders.scheduleSaved', 'Reminder schedule updated.')),
        onError: (err) =>
          toastApiError(err, t('reminders.updateFailed', 'Could not save the change')),
      },
    )
  }

  // `null` drops this record's own choice, so it follows the settings again.
  const setAutoCancel = (next: boolean | null) => {
    setReminder.mutate(
      { uuid, auto_cancel: next },
      {
        onError: (err) =>
          toastApiError(err, t('reminders.updateFailed', 'Could not save the change')),
      },
    )
  }

  const recipient = (status.recipient_email ?? '').trim()
  const noRecipient = t('reminders.noRecipient', 'No email address on the request')

  // A date, or the reason no reminder is coming.
  const nextReminder =
    status.has_active_request === false
      ? t('reminders.noOpenRequest', 'No document request open')
      : !recipient
        ? noRecipient
        : status.is_capped
          ? t('reminders.allSent', 'All reminders sent')
          : status.next_send_at
            ? status.is_due
              ? t('reminders.withinTheHour', 'Within the hour')
              : formatDateTime(status.next_send_at)
            : '-'

  const autoCancelLabel =
    resourceType === 'claim'
      ? t('reminders.closeIfNothing', 'Close if nothing arrives')
      : t('reminders.cancelIfNothing', 'Cancel if nothing arrives')

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-[460px]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <BellRing className="h-4 w-4 text-[var(--status-ok)]" />
            {t('reminders.title', 'Reminders')}
          </DialogTitle>
          <DialogDescription>
            {recipient
              ? t(
                  'reminders.goesTo',
                  'Reminders go to {{email}} until the documents arrive.',
                  { email: recipient },
                )
              : t(
                  'reminders.cannotSend',
                  'There is no email address on the request, so no reminders can go out.',
                )}
          </DialogDescription>
        </DialogHeader>

        <div className="-my-1">
          <Row icon={<Mail className="h-3.5 w-3.5" />} label={t('reminders.sentToLabel', 'Sent to')}>
            {recipient || noRecipient}
          </Row>
          <Row icon={<Repeat className="h-3.5 w-3.5" />} label={t('reminders.scheduleLabel', 'Schedule')}>
            {status.interval_days
              ? t('reminders.everyDays', 'Every day', { count: status.interval_days })
              : '-'}
          </Row>
          <Row icon={<Send className="h-3.5 w-3.5" />} label={t('reminders.sentLabel', 'Reminders sent')}>
            {status.max_reminders != null
              ? t('reminders.sentOf', '{{sent}} of {{max}}', {
                  sent: status.sent_count,
                  max: status.max_reminders,
                })
              : status.sent_count}
          </Row>
          <Row
            icon={<CalendarClock className="h-3.5 w-3.5" />}
            label={t('reminders.lastSentLabel', 'Last sent')}
          >
            {status.last_sent_at
              ? formatDateTime(status.last_sent_at)
              : t('reminders.notYet', 'Not yet')}
          </Row>
          <Row
            icon={<CalendarClock className="h-3.5 w-3.5" />}
            label={t('reminders.nextLabel', 'Next reminder')}
          >
            {nextReminder}
          </Row>
          <Row label={t('reminders.emailLabel', 'Email')}>{status.template_name || '-'}</Row>
        </div>

        {status.supports_cadence_override && (
          <>
            <Separator />
            <div className="space-y-3">
              <p className="text-[11px] uppercase tracking-[0.1em] text-[var(--ink-3)]">
                {t('reminders.scheduleLabel', 'Schedule')}
              </p>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1.5 block text-[12px]">
                    {t('reminders.everyDaysLabel', 'Every (days)')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={interval}
                    onChange={(e) => setInterval(e.target.value)}
                    placeholder={t('reminders.yourSetting', 'Your setting')}
                  />
                </div>
                <div>
                  <Label className="mb-1.5 block text-[12px]">
                    {t('reminders.maxLabel', 'Maximum reminders')}
                  </Label>
                  <Input
                    type="number"
                    min={1}
                    value={maxCount}
                    onChange={(e) => setMaxCount(e.target.value)}
                    placeholder={t('reminders.yourSetting', 'Your setting')}
                  />
                </div>
              </div>
              {cadenceDirty && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={saveCadence}
                  disabled={setReminder.isPending}
                >
                  {setReminder.isPending
                    ? t('reminders.saving', 'Saving...')
                    : t('reminders.saveSchedule', 'Save schedule')}
                </Button>
              )}
            </div>
          </>
        )}

        {status.supports_auto_cancel && (
          <>
            <Separator />
            <div className="flex items-center justify-between gap-4">
              <div>
                <p className="text-[13px] text-[var(--ink)]">{autoCancelLabel}</p>
                <p className="text-[11px] text-[var(--ink-3)]">
                  {status.auto_cancel_overridden === true ? (
                    <>
                      {t('reminders.setHere', 'Set here, not from your settings.')}{' '}
                      {canManage && (
                        <Button
                          variant="link"
                          size="xs"
                          className="h-auto p-0 text-[11px]"
                          onClick={() => setAutoCancel(null)}
                          disabled={setReminder.isPending}
                        >
                          {t('reminders.useSettings', 'Use your settings')}
                        </Button>
                      )}
                    </>
                  ) : status.auto_cancel_overridden === false ? (
                    t('reminders.followsSettings', 'Follows your settings.')
                  ) : (
                    t(
                      'reminders.afterLastReminder',
                      'After the last reminder, if the documents have not arrived.',
                    )
                  )}
                </p>
              </div>
              <Switch
                checked={status.auto_cancel_enabled}
                onCheckedChange={setAutoCancel}
                disabled={setReminder.isPending || !canManage}
                aria-label={autoCancelLabel}
              />
            </div>
          </>
        )}

        <DialogFooter className="mt-2">
          <Button variant="ghost" onClick={() => onOpenChange(false)}>
            {t('common.close', 'Close')}
          </Button>
          {status.enabled && canManage && (
            <Button
              variant="destructive"
              onClick={() => setConfirmCancel(true)}
              disabled={cancelReminder.isPending}
            >
              {t('reminders.turnOff', 'Turn off reminders')}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>

      {/* Nested confirm - conditionally mounted (Base UI dialogs don't reliably
          close on an always-mounted `open` toggle). */}
      {confirmCancel && (
        <Dialog open onOpenChange={(o) => !o && setConfirmCancel(false)}>
          <DialogContent className="max-w-[400px]">
            <DialogHeader>
              <DialogTitle>{t('reminders.turnOffTitle', 'Turn off reminders?')}</DialogTitle>
              <DialogDescription>
                {t(
                  'reminders.turnOffBody',
                  'No more reminder emails go out. You can turn them back on at any time.',
                )}
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button
                variant="ghost"
                onClick={() => setConfirmCancel(false)}
                disabled={cancelReminder.isPending}
              >
                {t('reminders.keepOn', 'Keep them on')}
              </Button>
              <Button
                variant="destructive"
                onClick={() =>
                  cancelReminder.mutate(
                    { uuid },
                    {
                      onSuccess: () => {
                        toast.success(t('reminders.turnedOff', 'Reminders turned off.'))
                        setConfirmCancel(false)
                        onOpenChange(false)
                      },
                      onError: (err) => {
                        toastApiError(
                          err,
                          t('reminders.turnOffFailed', 'Could not turn off reminders'),
                        )
                        setConfirmCancel(false)
                      },
                    },
                  )
                }
                disabled={cancelReminder.isPending}
              >
                {cancelReminder.isPending
                  ? t('reminders.turningOff', 'Turning off...')
                  : t('reminders.turnOffConfirm', 'Turn off')}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      )}
    </Dialog>
  )
}
